import { COMPONENT_TYPES } from '../config/componentConfig';
import { buildPrompt } from '../utils/promptBuilder';
import imageService from '../../../services/imageService';
import axios from '../../../services/api';
import { handleUnifiedError } from '../../../utils/unifiedErrorHandler';
import { API_BASE_URL } from '../../../lib/constants';

export const normalizeGenerationResult = (result, finalPrompt, options = {}) => {
  const useAbsoluteUrl = options.useAbsoluteUrl || false;
  const baseUrl = options.baseUrl || '';
  const toUrl = (url) => {
    if (!url) return '';
    if (!useAbsoluteUrl) return url;
    if (url.startsWith('http')) return url;
    return `${baseUrl}${url}`;
  };
  if (Array.isArray(result.imageUrls)) {
    return result.imageUrls.map((url, index) => {
      const share = Array.isArray(result.shareUrls) ? result.shareUrls[index] : result.shareUrl;
      return {
        url: toUrl(url),
        prompt: finalPrompt,
        id: result.id ? `${result.id}-${index + 1}` : `image-${index + 1}`,
        shareUrl: share || ''
      };
    });
  }
  if (result.imageUrl) {
    return {
      url: toUrl(result.imageUrl),
      prompt: finalPrompt,
      id: result.id,
      shareUrl: result.shareUrl || ''
    };
  }
  return null;
};

export default function useGenerate({
  components,
  promptConfig,
  additionalParams,
  toolId,
  componentValues,
  componentValidations,
  setIsGenerating,
  setGenerationProgress,
  setGeneratedImage,
  setError,
  startPolling,
  stopPolling,
  checkAuthAndExecute,
  t
}) {
  const buildTagParams = () => {
    const tagParams = {};
    components.forEach(component => {
      if (component.type !== COMPONENT_TYPES.TAG_SELECTOR) {
        return;
      }
      const dictName = component.props && component.props.dictName;
      if (!dictName) {
        return;
      }
      const value = componentValues[component.id];
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        return;
      }
      if (Array.isArray(value)) {
        tagParams[dictName] = value.join(',');
      } else {
        tagParams[dictName] = value;
      }
    });
    return tagParams;
  };

  const buildFinalPrompt = () => {
    if (!promptConfig) return '';
    return buildPrompt(promptConfig, componentValues, components);
  };

  const validateAllComponents = () => {
    let allValid = true;
    const errors = [];

    components.forEach(component => {
      const value = componentValues[component.id];
      const validation = componentValidations[component.id];

      if (component.required) {
        if (component.type === COMPONENT_TYPES.IMAGE_UPLOAD) {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            allValid = false;
            errors.push(`${component.title || component.id} ${t('aiToolTemplate.imageUpload.validation.required')}`);
          }
        } else if (component.type === COMPONENT_TYPES.PROMPT_INPUT) {
          if (!value || !value.trim()) {
            allValid = false;
            errors.push(`${component.title || component.id} ${t('aiToolTemplate.promptInput.validation.required')}`);
          }
        } else if (component.type === COMPONENT_TYPES.PLACE_PICKER) {
          if (!value || !value.coords) {
            allValid = false;
            errors.push(`${component.title || component.id} ${t('aiToolTemplate.placePicker.validation.required')}`);
          }
        }
      }

      if (validation && !validation.isValid) {
        allValid = false;
        errors.push(validation.message);
      }
    });

    return { allValid, errors };
  };

  const resolveSizeValue = () => {
    const photoSizeComp = components.find(c => c.id === 'photoSize');
    if (photoSizeComp) {
      const selected = componentValues['photoSize'] || photoSizeComp.defaultValue;
      if (selected) {
        const sizeMap = {
          oneInch: '25:35',
          twoInch: '35:53',
          passport: '35:45',
          visa: '51:51'
        };
        return sizeMap[selected] || selected;
      }
    }

    const sizeSelector = components.find(c => c.type === COMPONENT_TYPES.SIZE_SELECTOR);
    if (sizeSelector) {
      const selectedSize = componentValues[sizeSelector.id] || sizeSelector.defaultValue || 'Auto';
      if (selectedSize) return selectedSize;
    }

    if (typeof additionalParams?.size === 'string' && additionalParams.size) {
      return additionalParams.size;
    }

    return null;
  };

  const resolveMapInfo = () => {
    const mapComponent = components.find(c => c.type === COMPONENT_TYPES.PLACE_PICKER);
    if (mapComponent) {
      return componentValues[mapComponent.id];
    }
    return;
  };

  const handleGenerate = () => {
    const validation = validateAllComponents();
    if (!validation.allValid) {
      const errorMessage = validation.errors[0];
      setError(errorMessage);
      import('react-hot-toast').then(({ default: toast }) => toast.error(errorMessage));
      return;
    }

    setError('');

    checkAuthAndExecute(async () => {
      try {
        setIsGenerating(true);
        setGenerationProgress(0);
        setGeneratedImage(null);

        let result;
        const finalPrompt = buildFinalPrompt();

        const hasImageUpload = components.some(c => c.type === COMPONENT_TYPES.IMAGE_UPLOAD);
        const hasValidPrompt = !!(finalPrompt && finalPrompt.trim() !== '');
        const tagParams = buildTagParams();
        const filteredTagParams = Object.fromEntries(
          Object.entries(tagParams).filter(([key]) => key !== 'model' && key !== 'aspectRatio' && key !== 'resolution')
        );

        if (!hasImageUpload && !hasValidPrompt) {
          setIsGenerating(false);
          setGenerationProgress(0);
          const errorMessage = t('aiToolTemplate.promptRequired') || '请输入提示词';
          setError(errorMessage);
          import('react-hot-toast').then(({ default: toast }) => toast.error(errorMessage));
          return;
        }

        const sizeValue = resolveSizeValue();

        let finalAspectRatio = null;
        let finalModel = additionalParams?.model;
        let finalResolution = undefined;

        if (sizeValue && typeof sizeValue === 'object' && !Array.isArray(sizeValue)) {
          finalAspectRatio = sizeValue.aspectRatio ?? null;
          if (typeof sizeValue.model === 'string' && sizeValue.model) finalModel = sizeValue.model;
          if (typeof sizeValue.resolution === 'string' && sizeValue.resolution) finalResolution = sizeValue.resolution;
        } else if (typeof sizeValue === 'string' && sizeValue) {
          finalAspectRatio = sizeValue;
        }

        if (finalAspectRatio && typeof finalAspectRatio !== 'string') {
          finalAspectRatio = null;
        }
        if (finalModel && typeof finalModel !== 'string') {
          finalModel = undefined;
        }
        if (finalResolution && typeof finalResolution !== 'string') {
          finalResolution = undefined;
        }

        const mapInfo = resolveMapInfo();
        const mapInfoPayload = mapInfo ? JSON.stringify(mapInfo) : null;

        if (hasImageUpload) {
          const imageComponent = components.find(c => c.type === COMPONENT_TYPES.IMAGE_UPLOAD);
          const imageData = imageComponent ? componentValues[imageComponent.id] : [];
          if (!imageData || (Array.isArray(imageData) && imageData.length === 0)) {
            setGenerationProgress(0);
            const errorMessage = t('aiToolTemplate.imageRequired') || '请上传图片';
            setError(errorMessage);
            import('react-hot-toast').then(({ default: toast }) => toast.error(errorMessage));
            return;
          }

          let imageFiles = [];
          if (Array.isArray(imageData)) {
            imageFiles = imageData.map(item => item.file).filter(file => file instanceof File);
          } else if (imageData && imageData.file instanceof File) {
            imageFiles = [imageData.file];
          }

          if (imageFiles.length === 0) {
            setGenerationProgress(0);
            const errorMessage = t('aiToolTemplate.imageRequired') || '请上传图片';
            setError(errorMessage);
            import('react-hot-toast').then(({ default: toast }) => toast.error(errorMessage));
            return;
          }
          const requestData = {
            images: imageFiles,
            prompt: finalPrompt,
            negativePrompt: additionalParams?.negativePrompt || '',
            ...filteredTagParams,
            aspectRatio: finalAspectRatio,
            steps: additionalParams?.steps || 20,
            cfgScale: additionalParams?.cfgScale || 7.5,
            strength: additionalParams?.strength,
            imageStrength: additionalParams?.imageStrength,
            model: finalModel || 'Gemini',
            resolution: finalResolution,
            toolId
          };
          if (requestData.aspectRatio && typeof requestData.aspectRatio === 'object') {
            const settings = requestData.aspectRatio;
            requestData.aspectRatio = settings.aspectRatio || null;
            requestData.model = settings.model || requestData.model;
            requestData.resolution = settings.resolution || requestData.resolution;
          }
          if (mapInfoPayload) {
            requestData.mapInfo = mapInfoPayload;
          }
          result = await imageService.editImage(requestData);
        } else {
          const requestData = {
            prompt: finalPrompt,
            negativePrompt: additionalParams?.negativePrompt || '',
            ...filteredTagParams,
            model: finalModel || 'Gemini',
            aspectRatio: finalAspectRatio,
            resolution: finalResolution,
            steps: additionalParams?.steps || 20,
            cfgScale: additionalParams?.cfgScale || 7.0,
            seed: additionalParams?.seed || null,
            toolId,
          };
          if (requestData.aspectRatio && typeof requestData.aspectRatio === 'object') {
            const settings = requestData.aspectRatio;
            requestData.aspectRatio = settings.aspectRatio || null;
            requestData.model = settings.model || requestData.model;
            requestData.resolution = settings.resolution || requestData.resolution;
          }
          if (mapInfoPayload) {
            requestData.mapInfo = mapInfoPayload;
          }
          const response = await axios.post('/generate-image', requestData);
          result = response.data;
        }

        if (result && result.id) {
          import('react-hot-toast').then(({ default: toast }) => toast.success(t('aiToolTemplate.generationStarted')));
          if (result.status === 'processing') {
            startPolling(result.id, (progress) => {
              setGenerationProgress(progress);
            });
          } else if (result.status === 'completed') {
            setGenerationProgress(100);
            const value = normalizeGenerationResult(result, finalPrompt, {
              useAbsoluteUrl: false,
              baseUrl: API_BASE_URL
            });
            if (value) {
              setGeneratedImage(value);
            }
            setIsGenerating(false);
          }
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        setIsGenerating(false);
        setGenerationProgress(0);
        stopPolling();
        handleUnifiedError(error, t, { showToast: true, fallbackMessage: t('aiToolTemplate.generationFailed') });
      }
    });
  };

  return { handleGenerate, validateAllComponents };
}
